<script setup>
  import { computed, onMounted, onUnmounted, ref, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";

  import {
    WORKSPACE_MENU,
    getWorkspaceExam,
    isWorkspaceMenuActive,
    resolveWorkspaceMenu,
  } from "@/config/workspace";
  import api from "@/lib/api";
  import { useRoleStore } from "@/stores/role";
  import { getRoundLabel, getRoundPhase, useWorkspaceStore } from "@/stores/workspace";

  // 나의 검사 관리 LNB (US-4000): 상단 시험 카드(시험명 · 시험 구분 · 회차 선택) + 업무 메뉴
  // 회차는 본문 제목 카드의 "시험 선택" 줄과 같은 값(ws.roundId)을 바꾼다
  // 옆 손잡이로 접으면 아이콘 레일이 되고, 하위 메뉴는 플라이아웃으로 연다
  const route = useRoute();
  const router = useRouter();
  const roleStore = useRoleStore();
  const ws = useWorkspaceStore();

  const ICONS = {
    plan: '<path d="M4 5h12v11H4z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M4 8.5h12M7.5 3v3.5M12.5 3v3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
    items: '<path d="M5 3h7l3 3v11H5z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M7.5 10h5M7.5 13h3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
    rehearsal: '<circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="1.5"/><path d="m8.5 7.3 4 2.7-4 2.7z" fill="currentColor"/>',
    check: '<rect x="3" y="4" width="14" height="10" rx="1.5" stroke="currentColor" stroke-width="1.5"/><path d="M7 17h6M7.5 9l1.8 1.8L13 7.2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
    proctor: '<path d="M2.5 10s2.7-5 7.5-5 7.5 5 7.5 5-2.7 5-7.5 5-7.5-5-7.5-5z" stroke="currentColor" stroke-width="1.5"/><circle cx="10" cy="10" r="2.3" stroke="currentColor" stroke-width="1.5"/>',
    grading: '<path d="M4 16l1-4L13.5 3.5l3 3L8 15z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="m11.5 5.5 3 3" stroke="currentColor" stroke-width="1.5"/>',
    result: '<path d="M4 16V9M8.5 16V5M13 16v-5M17 16H3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
    members: '<circle cx="8" cy="7" r="3" stroke="currentColor" stroke-width="1.5"/><path d="M2.5 16.5c.6-2.9 2.8-4.5 5.5-4.5s4.9 1.6 5.5 4.5M13.5 4.3a2.8 2.8 0 0 1 0 5.4M15.5 12.4c1 .7 1.7 2 2 4.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  };

  const exam = computed(() => getWorkspaceExam(ws.examType));
  // 학생 나의 검사 관리: 시험 카드(시험명 · 시험 구분 표시만) + 응시하기 · 검사 결과
  const isStudent = computed(() => route.path.startsWith("/my-test/student"));
  const STUDENT_MENU = [
    { id: "stu-apply", label: "응시하기", icon: "check", route: "MyTestStudentApply" },
    { id: "stu-result", label: "검사 결과", icon: "result", route: "MyTestStudentResult" },
  ];

  // ---------- 시험 구분 · 회차 ----------
  const openPop = ref(null); // "category" | "round" | null
  const ctxRef = ref(null);
  const togglePop = (name) => {
    openPop.value = openPop.value === name ? null : name;
  };
  const selectCategory = (key) => {
    ws.setCategory(key);
    openPop.value = null;
  };
  // 회차: 제목 카드 회차 선택과 같은 회차 목록(화면별 회차 제한 포함). 좌측에는 '회차 전체' 항목을 두지 않는다
  const roundLabel = computed(() => {
    if (ws.roundsOfExam.length === 0) return ws.roundFilter?.emptyLabel || "등록된 회차 없음";
    return ws.roundId === "all" ? "회차 전체" : getRoundLabel(ws.currentRound);
  });
  const selectRound = (id) => {
    ws.setRound(id);
    openPop.value = null;
  };
  const openCategoryFromRail = () => {
    ws.setRail(false);
    setTimeout(() => {
      openPop.value = "category";
    }, 200);
  };

  // ---------- 메뉴 ----------
  const isVisible = (item) => {
    if (item.exams && !item.exams.includes(ws.examType)) return false;
    if (item.requiresSchoolAdmin && !roleStore.isSchoolAdmin) return false;
    // 채점 관리: 기존 채점관리 탭과 같은 기준(학교관리자 + 채점 결과 확인/피드백 대상)
    if (item.requiresGradingAccess && !(roleStore.isSchoolAdmin || roleStore.canViewFeedback)) {
      return false;
    }
    return true;
  };
  // 채점 관리 하위 메뉴 노출 대상 (config/workspace.js 의 access)
  const isChildVisible = (child) => {
    switch (child.access) {
      case "graderOnly":
        return (
          roleStore.hasGradingAccess &&
          !roleStore.isSchoolAdmin &&
          !roleStore.isGradeAdmin &&
          !ws.isGradingLeader
        );
      case "scoring":
        // 학교관리자는 채점 권한이 없으면 채점자 등록만 (GradingManage 섹션 규칙과 동일)
        return (
          roleStore.hasGradingAccess ||
          (!roleStore.isSchoolAdmin && (roleStore.isHomeroomTeacher || roleStore.isPrincipalOrVice))
        );
      case "schoolAdmin":
        return roleStore.isSchoolAdmin;
      case "leader":
        return ws.isGradingLeader;
      default:
        return true;
    }
  };
  const menu = computed(() =>
    isStudent.value ? STUDENT_MENU : resolveWorkspaceMenu(isChildVisible).filter(isVisible),
  );

  // 팀장 여부는 회차별 채점 배정으로 판단 → 회차가 바뀔 때마다 조회
  watch(
    () => [ws.examType, ws.currentRound?.exam_id, roleStore.hasGradingAccess],
    () => {
      if (roleStore.hasGradingAccess) ws.fetchGradingAssignments();
      else ws.clearGradingAssignments();
    },
    { immediate: true },
  );

  const isLinkActive = (link) => isWorkspaceMenuActive(link, route);
  const isGroupActive = (item) =>
    item.children ? item.children.some(isLinkActive) : isLinkActive(item);

  const openGroups = ref(new Set());
  const syncOpenGroups = () => {
    menu.value.forEach((item) => {
      if (item.children && isGroupActive(item)) openGroups.value.add(item.id);
    });
  };
  watch(() => route.fullPath, syncOpenGroups, { immediate: true });

  // 시험을 바꿨는데 현재 메뉴가 그 시험에 없으면(예: 채점 관리는 서논술형 전용) 시험 계획으로 이동
  watch(
    () => [ws.examType, route.fullPath],
    () => {
      const current = WORKSPACE_MENU.find((item) => isGroupActive(item));
      if (current?.exams && ws.examType && !current.exams.includes(ws.examType)) {
        router.replace({ name: "MyTestTeacherPlan" });
      }
    },
    { immediate: true },
  );

  const go = (link) => {
    closeFly();
    router.push({ name: link.route });
  };

  const handleItemClick = (item, event) => {
    if (!item.children) {
      go(item);
      return;
    }
    if (ws.rail) {
      openFly(item, event.currentTarget);
      return;
    }
    const next = new Set(openGroups.value);
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.add(item.id);
      // 펼칠 때 현재 메뉴가 아니면 첫 하위 메뉴로 이동 (시안 동작)
      if (!isGroupActive(item)) go(item.children[0]);
    }
    openGroups.value = next;
  };

  // ---------- 대상 지정 설문 카드 ----------
  // 메인 팝업과 같은 목록(대상자 + 설문 기간 + 미제출). 제출하면 목록에서 빠져 카드도 사라진다
  const surveys = ref([]);
  const fetchSurveys = async () => {
    // 대상 지정 설문은 교사용
    if (isStudent.value) return;
    try {
      const { data } = await api.post("/survey/tea/popupList", {});
      surveys.value = data?.success ? data.result?.list || [] : [];
    } catch (e) {
      surveys.value = [];
    }
  };
  // 시험에 묶인 설문(exam_id)은 LNB 에서 고른 회차가 그 시험일 때만, 시험 무관 설문은 항상 보인다
  const visibleSurveys = computed(() => {
    if (ws.rail || isStudent.value) return [];
    const examIds =
      ws.roundId === "all"
        ? ws.roundsOfExam.map((r) => r.exam_id)
        : [ws.currentRound?.exam_id].filter(Boolean);
    return surveys.value.filter((s) => !s.exam_id || examIds.includes(s.exam_id));
  });
  // "2026년 1학기 채움AI 실천학교 ○○ 설문" → 제목 "○○ 설문" / 부제 "2026년 1학기 · 채움AI 실천학교 선생님 대상"
  // 형식이 다르면 설문명 그대로 + 참여 기간
  const surveyCard = (survey) => {
    const name = survey.survey_name || "";
    const m = name.match(/^(\d{4}년\s*\d학기)\s+(채움AI\s*실천학교)\s+(.+)$/);
    if (m) return { title: m[3], sub: `${m[1]} · ${m[2]} 선생님 대상` };
    return { title: name, sub: `참여 기간 ${survey.start_date} ~ ${survey.end_date}` };
  };
  const joinSurvey = (survey) => {
    router.push({ path: "/survey", query: { survey_id: survey.survey_id } });
  };

  // ---------- 레일 플라이아웃 ----------
  const fly = ref(null); // { item, top, left, anchor }
  let flyTimer = null;
  const openFly = (item, anchor) => {
    const rect = anchor.getBoundingClientRect();
    fly.value = {
      item,
      anchor,
      left: rect.right + 8,
      top: Math.min(rect.top, window.innerHeight - 220),
    };
  };
  const closeFly = () => {
    clearTimeout(flyTimer);
    fly.value = null;
  };
  const handleItemEnter = (item, event) => {
    if (!ws.rail || !item.children || fly.value?.item === item) return;
    const anchor = event.currentTarget;
    clearTimeout(flyTimer);
    flyTimer = setTimeout(() => openFly(item, anchor), fly.value ? 220 : 80);
  };
  const handleItemLeave = () => clearTimeout(flyTimer);

  const handleDocumentMousedown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (openPop.value && !ctxRef.value?.contains(target)) openPop.value = null;
    if (fly.value && !target.closest(".ws-fly") && !fly.value.anchor.contains(target)) closeFly();
  };
  const handleKeydown = (event) => {
    if (event.key === "Escape") {
      openPop.value = null;
      closeFly();
    }
  };
  const handleScroll = () => {
    if (!fly.value) return;
    const rect = fly.value.anchor.getBoundingClientRect();
    fly.value = { ...fly.value, top: Math.min(rect.top, window.innerHeight - 220) };
  };

  watch(
    () => ws.rail,
    () => {
      closeFly();
      openPop.value = null;
    },
  );

  onMounted(() => {
    fetchSurveys();
    document.addEventListener("mousedown", handleDocumentMousedown);
    document.addEventListener("keydown", handleKeydown);
    window.addEventListener("scroll", handleScroll, true);
  });
  onUnmounted(() => {
    document.removeEventListener("mousedown", handleDocumentMousedown);
    document.removeEventListener("keydown", handleKeydown);
    window.removeEventListener("scroll", handleScroll, true);
  });
</script>

<template>
  <aside class="ws-lnb" :class="{ 'is-rail': ws.rail }" :aria-label="`${exam.short} 업무 메뉴`">
    <button
      type="button"
      class="ws-lnb__handle"
      :aria-label="ws.rail ? '메뉴 펼치기' : '메뉴 접기'"
      :aria-expanded="!ws.rail"
      @click="ws.setRail(!ws.rail)"
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <div class="ws-lnb__scroll">
      <!-- 시험 카드 -->
      <div v-if="!ws.rail" ref="ctxRef" class="ws-ctx">
        <h2>{{ exam.title }}</h2>
        <!-- 학생: 시험 구분(표준화시험 · 시범시험 등)만 고른다 (회차 선택 없음) -->
        <button
          type="button"
          class="ws-ctx__round"
          aria-haspopup="listbox"
          :aria-expanded="openPop === 'category'"
          aria-label="시험 구분 선택"
          @click="togglePop('category')"
        >
          <span>{{ ws.category.label }}</span>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="m2.5 4.5 3.5 3.5 3.5-3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div v-if="openPop === 'category'" class="ws-ctx__pop" role="listbox" aria-label="시험 구분 선택">
          <p v-if="isStudent">시험 구분을 바꾸면 응시하기 · 검사 결과가 해당 구분 기준으로 바뀝니다.</p>
          <p v-else>시험 구분을 바꾸면 시험 계획부터 결과 관리까지 모든 메뉴가 해당 구분 기준으로 바뀝니다.</p>
          <button
            v-for="c in ws.categories"
            :key="c.key"
            type="button"
            role="option"
            :aria-selected="ws.category.key === c.key"
            :class="{ 'is-on': ws.category.key === c.key }"
            @click="selectCategory(c.key)"
          >
            <span><b>{{ c.label }}</b><small v-if="!isStudent">회차 {{ ws.rounds.filter(r => r.exam_type === ws.examType && c.se.includes(r.exam_se)).length }}개</small></span>
            <i class="ck"></i>
          </button>
        </div>
        <button
          v-if="!isStudent"
          type="button"
          class="ws-ctx__round"
          aria-haspopup="listbox"
          :aria-expanded="openPop === 'round'"
          aria-label="회차 선택"
          :disabled="ws.roundsOfExam.length === 0"
          @click="togglePop('round')"
        >
          <span>{{ roundLabel }}</span>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="m2.5 4.5 3.5 3.5 3.5-3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div v-if="openPop === 'round'" class="ws-ctx__pop" role="listbox" aria-label="회차 선택">
          <p>회차를 바꾸면 모든 메뉴가 해당 회차 기준으로 바뀝니다.</p>
          <button
            v-for="r in ws.roundsOfExam"
            :key="r.exam_id"
            type="button"
            role="option"
            :aria-selected="ws.roundId === r.exam_id"
            :class="{ 'is-on': ws.roundId === r.exam_id }"
            @click="selectRound(r.exam_id)"
          >
            <span><b>{{ getRoundLabel(r) }}</b><small>{{ getRoundPhase(r)?.label }}</small></span>
            <i class="ck"></i>
          </button>
        </div>
      </div>

      <!-- 레일: 축약 시험 카드 -->
      <button
        v-else
        type="button"
        class="ws-ctx-mini"
        title="시험 구분 선택"
        @click="openCategoryFromRail"
      >
        <span class="sq">{{ exam.short }}</span>
        <small>{{ ws.category.short }}</small>
      </button>

      <ul class="ws-nav">
        <li
          v-for="item in menu"
          :key="item.id"
          :class="{
            'is-cur': isGroupActive(item),
            'is-open': openGroups.has(item.id),
            'has-divider': item.divider,
          }"
        >
          <button
            type="button"
            class="ws-nav__n1"
            :class="{ 'is-flying': fly?.item === item }"
            :aria-expanded="item.children ? openGroups.has(item.id) : undefined"
            :aria-current="!item.children && isGroupActive(item) ? 'page' : undefined"
            :title="ws.rail ? item.label : undefined"
            @click="handleItemClick(item, $event)"
            @mouseenter="handleItemEnter(item, $event)"
            @mouseleave="handleItemLeave"
          >
            <svg class="ic" viewBox="0 0 20 20" fill="none" aria-hidden="true" v-html="ICONS[item.icon]" />
            <span class="lb">{{ item.label }}</span>
            <svg v-if="item.children" class="car" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="m3.5 5.5 3.5 3.5 3.5-3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div v-if="item.children && !ws.rail" class="ws-nav__sub">
            <a
              v-for="child in item.children"
              :key="child.id"
              href="#none"
              :class="{ 'is-on': isLinkActive(child) }"
              :aria-current="isLinkActive(child) ? 'page' : undefined"
              @click.prevent="go(child)"
            >
              {{ child.label }}
            </a>
          </div>
        </li>
      </ul>

      <div v-for="survey in visibleSurveys" :key="survey.survey_id" class="ws-survey">
        <span class="ws-survey__badge">설문</span>
        <p class="ws-survey__title">{{ surveyCard(survey).title }}</p>
        <p class="ws-survey__sub">{{ surveyCard(survey).sub }}</p>
        <button type="button" class="ws-survey__btn" @click="joinSurvey(survey)">
          {{ survey.in_progress_yn === "Y" ? "이어서 참여하기" : "설문 참여하기" }}
          <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M4.5 9.5 9.5 4.5M5.5 4.5h4v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  </aside>

  <Teleport to="body">
    <div
      v-if="fly"
      class="ws-fly"
      role="menu"
      :style="{ left: `${fly.left}px`, top: `${fly.top}px` }"
    >
      <b>{{ fly.item.label }}</b>
      <a
        v-for="child in fly.item.children"
        :key="child.id"
        href="#none"
        role="menuitem"
        :class="{ 'is-on': isLinkActive(child) }"
        @click.prevent="go(child)"
      >
        {{ child.label }}
      </a>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
  $grad: linear-gradient(100deg, #7b52f6 0%, #3f78fb 38%, #1893f3 70%, #2ad0d8 100%);

  .ws-lnb {
    position: sticky;
    top: var(--ws-header-h);
    align-self: flex-start;
    flex-shrink: 0;
    width: 256px;
    height: calc(100vh - var(--ws-header-h));
    background: #fff;
    border-right: 1px solid #e2e6ea;
    transition: width 0.18s ease;
    z-index: 30;

    &.is-rail {
      width: 76px;
    }

    :where(button) {
      font: inherit;
      color: inherit;
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
    }

    &__scroll {
      height: 100%;
      overflow-y: auto;
      padding: 16px 12px 24px;
      scrollbar-width: thin;
    }

    &.is-rail &__scroll {
      padding: 14px 8px;
    }

    &__handle {
      position: absolute;
      top: 24px;
      right: -18px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 52px;
      border: 1px solid #e2e6ea !important;
      border-left: 0 !important;
      border-radius: 0 10px 10px 0;
      background: #fff !important;
      color: #8a949e !important;
      box-shadow: 3px 0 8px rgba(20, 40, 90, 0.05);
      z-index: 1;

      &:hover {
        color: #0a7cff !important;
      }

      svg {
        transition: transform 0.18s;
      }
    }

    &.is-rail &__handle svg {
      transform: rotate(180deg);
    }
  }

  /* 시험 카드 */
  .ws-ctx {
    position: relative;
    margin-bottom: 16px;
    padding: 16px;
    border-radius: 16px;
    background: $grad;
    color: #fff;
    box-shadow: 0 8px 20px rgba(60, 90, 230, 0.18);

    h2 {
      font-size: 15px;
      font-weight: 700;
      line-height: 1.4;
      text-align: center;
    }

    &__round {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 36px;
      margin-top: 12px;
      padding: 0 28px !important;
      border-radius: 60px;
      background: rgba(255, 255, 255, 0.18) !important;
      color: #fff !important;
      font-size: 13px;
      font-weight: 700;

      span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      svg {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
      }

      &:hover,
      &[aria-expanded="true"] {
        background: rgba(255, 255, 255, 0.28) !important;
      }

      // 구분 · 회차 두 줄 (시안)
      & + & {
        margin-top: 8px;
      }

      &:disabled {
        opacity: 0.7;
        cursor: default;
      }
    }

    &__pop {
      position: absolute;
      left: 0;
      right: 0;
      top: calc(100% - 10px);
      z-index: 45;
      max-height: 360px;
      overflow-y: auto;
      padding: 8px;
      background: #fff;
      color: #464c53;
      border: 1px solid #e2e6ea;
      border-radius: 14px;
      box-shadow: 0 14px 30px rgba(20, 40, 90, 0.16);

      p {
        padding: 6px 8px 8px;
        font-size: 12px;
        color: #8a949e;
      }

      button {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        padding: 10px !important;
        border-radius: 9px;
        text-align: left;

        &:hover {
          background: #f0f7ff !important;
        }

        &.is-on {
          background: #e9f3ff !important;
        }

        b {
          display: block;
          font-size: 13px;
          color: #1e2124;
        }

        small {
          font-size: 11px;
          color: #8a949e;
        }
      }

      .ck {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 1.5px solid #c1cad4;
      }

      button.is-on .ck {
        border: 5px solid #0a7cff;
      }
    }
  }

  .ws-ctx-mini {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 100%;
    margin-bottom: 8px;
    padding: 6px 0 12px !important;
    border-bottom: 1px solid #f0f2f4 !important;

    .sq {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: $grad;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      line-height: 1.2;
      text-align: center;
    }

    small {
      font-size: 10px;
      font-weight: 700;
      line-height: 1.3;
      color: #0a7cff;
      text-align: center;
    }
  }

  /* 메뉴 */
  .ws-nav {
    list-style: none;

    > li {
      margin-bottom: 2px;

      &.has-divider {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #f0f2f4;
      }
    }

    &__n1 {
      position: relative;
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      height: 44px;
      padding: 0 10px !important;
      border-radius: 9px;
      font-size: 14px;
      font-weight: 600;
      color: #1e2124 !important;
      text-align: left;

      &:hover,
      &.is-flying {
        background: #f5f7fa !important;
      }

      .ic {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        color: #8a949e;
      }

      .lb {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .car {
        width: 14px;
        height: 14px;
        color: #c1cad4;
        transition: transform 0.15s;
      }
    }

    li.is-open > &__n1 .car {
      transform: rotate(180deg);
    }

    li.is-cur > &__n1 {
      background: #e9f3ff !important;
      color: #0a7cff !important;

      .ic {
        color: #0a7cff;
      }
    }

    &__sub {
      display: none;
      padding: 2px 0 6px 40px;

      a {
        display: block;
        padding: 7px 10px;
        border-radius: 7px;
        font-size: 13px;
        color: #464c53;

        &:hover {
          background: #f5f7fa;
          color: #1e2124;
        }

        &.is-on {
          background: #f3f8ff;
          color: #0a7cff;
          font-weight: 700;
        }
      }
    }

    li.is-open > &__sub {
      display: block;
    }
  }

  /* 레일 */
  /* 대상 지정 설문 카드 */
  .ws-survey {
    margin-top: 20px;
    padding: 16px;
    border: 1px solid #ddd3fb;
    border-radius: 14px;
    background: linear-gradient(160deg, #f6f2ff 0%, #ece6fd 100%);

    &__badge {
      display: inline-block;
      padding: 2px 10px;
      border: 1px solid #c9b8f8;
      border-radius: 20px;
      background: #fff;
      color: #6a3df0;
      font-size: 12px;
      font-weight: 700;
    }

    &__title {
      margin-top: 10px;
      color: #1e2124;
      font-size: 15px;
      font-weight: 700;
      line-height: 1.45;
      word-break: keep-all;
    }

    &__sub {
      margin-top: 8px;
      color: #6d7882;
      font-size: 12.5px;
      line-height: 1.5;
      word-break: keep-all;
    }

    &__btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 100%;
      height: 40px;
      margin-top: 14px;
      border-radius: 10px;
      background: #6a3df0 !important;
      color: #fff !important;
      font-size: 14px;
      font-weight: 700;

      &:hover {
        background: #5a2fe0 !important;
      }

      svg {
        width: 14px;
        height: 14px;
      }
    }
  }

  .ws-lnb.is-rail .ws-nav__n1 {
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    height: 60px;
    padding: 6px 2px !important;

    .lb {
      flex: none;
      font-size: 10px;
      font-weight: 500;
      line-height: 1.25;
      text-align: center;
      white-space: normal;
      overflow: visible;
    }

    .car {
      display: none;
    }
  }

  .ws-fly {
    position: fixed;
    z-index: 50;
    min-width: 190px;
    padding: 8px;
    background: #fff;
    border: 1px solid #e2e6ea;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);

    // 레일 버튼 → 메뉴로 옮기는 사이 빈틈
    &::before {
      content: "";
      position: absolute;
      left: -10px;
      top: 0;
      bottom: 0;
      width: 10px;
    }

    b {
      display: block;
      margin-bottom: 4px;
      padding: 6px 10px 8px;
      border-bottom: 1px solid #f0f2f4;
      font-size: 13px;
      color: #1e2124;
    }

    a {
      display: block;
      padding: 8px 10px;
      border-radius: 7px;
      font-size: 13px;
      color: #464c53;

      &:hover {
        background: #f5f7fa;
      }

      &.is-on {
        color: #0a7cff;
        font-weight: 700;
      }
    }
  }
</style>
